import type { MeResponse } from '@tabel/shared/schemas/auth';
import { useMutation } from '@tanstack/react-query';
import { Bot, ChevronLeft, CircleAlert, Maximize2, Plus } from 'lucide-react';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { threadFromDay } from '../../lib/agent-api';
import { ApiError } from '../../lib/errors';
import { NAV_PATH } from '../../lib/nav';
import { ErrorCodes, errorCode, errorSentence } from '../ErrorToast';
import { Button, Callout, IconButton, SidePanel } from '../ui';
import { AgentMenu } from './AgentMenu';
import { Conversation } from './Conversation';
import { OpenOnRules, useProjectView } from './ProjectPanel';
import { STRIP_LINK } from './StateStrip';
import { HINTS, LABEL, TITLE, say } from './texts';
import { type AgentChoice, defaultChoice, readChoice, saveChoice, useAgentComputers } from './useThread';

/*
 * Правая панель «Помощник» (T076, T090; contracts/ui.md §1; FR-031, FR-034, FR-038–FR-040, FR-052; мокап Main.dc.html).
 * - Открывается кнопкой «Помощник» в шапке листа (`AssistantButton`), «Спросить агента» на правиле (поле заполняется
 *   вопросом о правиле) и «Спросить агента об этом дне» на карточке дня (`POST /api/agent/threads/from-day`: новый
 *   разговор, поле заполнено ссылкой и вопросом; сообщение не уходит само).
 * - `SidePanel` дизайн-системы: 620 px у правого края поверх листа, левый край тянется; до 900 px — на весь экран.
 *   Шапка 48 px: «Помощник / <название>» → агент ▾ → «Новый разговор» → «Развернуть» (на страницу «Помощник») →
 *   «Закрыть (Esc)».
 * - Режим «проект»: «Открыть проект» на карточке проекта показывает проект в той же панели, с кнопкой «К разговору»
 *   (одна панель на объект, ux.md §4). Публикует человек на странице правил.
 * - Разговор панели помнится в браузере (`tabel.assistant.thread`): панель открывается на нём же. «Новый разговор» —
 *   разговор с нуля на выбранном агенте; он создаётся первым сообщением.
 * - Подсказки — по странице (FR-038): правила, табель, день.
 * Помощника нет у ролей без права `agent.thread`, без права «ИИ-агент» и при выключенной функции 006.
 */

const THREAD_KEY = 'tabel.assistant.thread';

function readThread(): string | null {
  try {
    return localStorage.getItem(THREAD_KEY);
  } catch {
    return null;
  }
}

function saveThread(id: string | null): void {
  try {
    if (id) localStorage.setItem(THREAD_KEY, id);
    else localStorage.removeItem(THREAD_KEY);
  } catch {
    // хранилище недоступно — разговор помнится до перезагрузки
  }
}

interface PanelState {
  open: boolean;
  mode: 'chat' | 'project';
  planId: number | null;
  threadId: string | null;
  draft: { text: string; tick: number } | null;
  /** «Спросить агента об этом дне»: разговор создаётся. */
  day: { date: string; personId: string } | null;
}

export interface AssistantApi {
  isOpen: boolean;
  /** Открыть панель; `draft` — текст в поле сообщения («Спросить агента»). */
  open: (o?: { draft?: string }) => void;
  /** «Спросить агента об этом дне» (FR-052). */
  askAboutDay: (day: { date: string; personId: string }) => void;
  toggle: () => void;
  close: () => void;
}

interface Ctx {
  available: boolean;
  api: AssistantApi;
  state: PanelState;
  set: (patch: Partial<PanelState>) => void;
  tz: string;
}

const AssistantContext = createContext<Ctx | null>(null);

/** Помощник на этой странице: `null` — помощника нет (нет права, функция выключена, вне каркаса). */
export function useAssistant(): AssistantApi | null {
  const c = useContext(AssistantContext);
  return c?.available ? c.api : null;
}

export function AssistantProvider({ me, children }: { me: MeResponse; children: ReactNode }) {
  const computers = useAgentComputers();
  const [state, setState] = useState<PanelState>(() => ({
    open: false,
    mode: 'chat',
    planId: null,
    threadId: readThread(),
    draft: null,
    day: null,
  }));
  const tick = useRef(0);
  const set = useCallback((patch: Partial<PanelState>) => {
    setState((s) => {
      const next = { ...s, ...patch };
      if (next.threadId !== s.threadId) saveThread(next.threadId);
      return next;
    });
  }, []);
  const api = useMemo<AssistantApi>(
    () => ({
      isOpen: state.open,
      open: (o) =>
        set({
          open: true,
          mode: 'chat',
          ...(o?.draft ? { draft: { text: o.draft, tick: ++tick.current } } : {}),
        }),
      askAboutDay: (day) => set({ open: true, mode: 'chat', day, draft: null }),
      toggle: () => set({ open: !state.open }),
      close: () => set({ open: false }),
    }),
    [set, state.open],
  );
  const value = useMemo(
    () => ({ available: computers.available, api, state, set, tz: me.object.tzOffset }),
    [computers.available, api, state, set, me.object.tzOffset],
  );
  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

/** Кнопка «Помощник» в шапке листа (мокап: вторичная, на телефоне — только значок). */
export function AssistantButton() {
  const c = useContext(AssistantContext);
  const { pathname } = useLocation();
  if (!c?.available || pathname.startsWith(NAV_PATH.assistant)) return null;
  return (
    <Button
      size="sm"
      icon={Bot}
      aria-pressed={c.state.open}
      aria-label={LABEL.open}
      title={LABEL.open}
      onClick={c.api.toggle}
      className="max-sm:h-10 max-sm:w-10 max-sm:px-0"
    >
      <span className="max-sm:hidden">{LABEL.open}</span>
    </Button>
  );
}

/** Подсказки по странице, где открыта панель; у разговора о дне — свои (`Conversation`). */
function hintsOf(pathname: string): readonly string[] {
  return pathname.startsWith('/timesheets') ? HINTS.timesheet : HINTS.rules;
}

/** Разговор не создан с карточки дня: причина, код ошибки и одно действие. */
function DayError({
  error,
  onRetry,
  onMyAgents,
}: { error: unknown; onRetry: () => void; onMyAgents: () => void }) {
  const notReady = error instanceof ApiError && error.code === 'AGENT_NOT_READY';
  const requestId = error instanceof ApiError ? error.requestId : null;
  const code = errorCode(error);
  return (
    <div className="shrink-0 px-6 pt-3 max-sm:px-4">
      <div role="alert">
        <Callout tone="bad" icon={CircleAlert}>
          <div className="flex flex-col gap-1.5">
            <p>
              <span className="font-semibold">{TITLE.dayError}.</span> {errorSentence(error)}
            </p>
            {(requestId || code) && (
              <p className="text-[12px] text-ink-2">
                {LABEL.errorCode} <ErrorCodes requestId={requestId} code={code} />
              </p>
            )}
            <p>
              <button type="button" className={STRIP_LINK} onClick={notReady ? onMyAgents : onRetry}>
                {notReady ? LABEL.myAgents : LABEL.retry}
              </button>
            </p>
          </div>
        </Callout>
      </div>
    </div>
  );
}

/** Правая панель: разговор или проект. Ставится внутрь места для панели листа (`SidePanelHost`). */
export function AssistantPanel() {
  const c = useContext(AssistantContext);
  if (!c?.available || !c.state.open) return null;
  return c.state.mode === 'project' && c.state.planId !== null ? (
    <ProjectMode ctx={c} planId={c.state.planId} />
  ) : (
    <ChatMode ctx={c} />
  );
}

function ProjectMode({ ctx, planId }: { ctx: Ctx; planId: number }) {
  const v = useProjectView(planId, ctx.tz, ctx.api.close);
  return (
    <SidePanel
      open
      onClose={ctx.api.close}
      label={TITLE.panel}
      title={
        <span className="flex min-w-0 items-center gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            icon={ChevronLeft}
            className="-ml-2"
            onClick={() => ctx.set({ mode: 'chat' })}
          >
            {LABEL.toThread}
          </Button>
          <span className="truncate">{say.project(planId)}</span>
        </span>
      }
      expandable={false}
      actions={<OpenOnRules planId={planId} onLeave={ctx.api.close} />}
      status={v.status}
      footer={v.footer}
    >
      {v.body}
    </SidePanel>
  );
}

function ChatMode({ ctx }: { ctx: Ctx }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const computers = useAgentComputers();
  const field = useRef<HTMLTextAreaElement>(null);
  const [choice, setChoice] = useState<AgentChoice | null>(() => readChoice());
  const pick = defaultChoice(computers.list, choice);
  const { state, set } = ctx;

  const day = useMutation({ mutationFn: threadFromDay });
  const dayReq = state.day;
  const startDay = day.mutate;
  useEffect(() => {
    if (!dayReq) return;
    set({ day: null });
    startDay(dayReq, {
      onSuccess: (r) => set({ threadId: r.threadId, draft: { text: r.draft, tick: Date.now() } }),
      onError: () => set({ threadId: null }),
    });
  }, [dayReq, set, startDay]);
  const lastDay = useRef<{ date: string; personId: string } | null>(null);
  if (dayReq) lastDay.current = dayReq;

  const leave = (to: string) => {
    ctx.api.close();
    navigate(to);
  };
  const newThread = () => {
    day.reset();
    set({ threadId: null, draft: null, mode: 'chat' });
  };

  if (day.isPending)
    return (
      <SidePanel
        open
        onClose={ctx.api.close}
        label={TITLE.panel}
        crumb={TITLE.panel}
        title={TITLE.newThread}
        expandable={false}
      >
        <div aria-busy="true" aria-label={LABEL.loading} className="m-auto text-[13px] text-ink-2">
          {LABEL.loading}
        </div>
      </SidePanel>
    );

  return (
    <Conversation
      threadId={state.threadId}
      // Панель на страницах правил и табеля — `rules`; разговор о дне создаёт `from-day` (`day`).
      page="rules"
      choice={pick}
      draft={state.draft}
      hints={hintsOf(pathname)}
      tz={ctx.tz}
      layout="panel"
      inputRef={field}
      onCreated={(t) => set({ threadId: t.id })}
      onOpenProject={(planId) => set({ mode: 'project', planId })}
      onNewThread={newThread}
      onOpenMyAgents={() => leave(NAV_PATH.myAgents)}
    >
      {(parts) => (
        <SidePanel
          open
          onClose={ctx.api.close}
          label={TITLE.panel}
          crumb={TITLE.panel}
          title={parts.thread?.title ?? (state.threadId ? LABEL.loading : TITLE.newThread)}
          expandable={false}
          initialFocus={field}
          actions={
            <>
              <AgentMenu
                computers={computers.list}
                value={
                  parts.thread ? { connectorId: parts.thread.connectorId, agent: parts.thread.agent } : pick
                }
                locked={state.threadId !== null}
                onPick={(c) => {
                  saveChoice(c);
                  setChoice(c);
                  // Разговор уже идёт с другим агентом: выбранный агент начинает новый разговор.
                  if (state.threadId !== null) newThread();
                }}
              />
              <IconButton icon={Plus} label={LABEL.newThread} onClick={newThread} />
              <IconButton
                icon={Maximize2}
                label={LABEL.expand}
                onClick={() =>
                  leave(
                    state.threadId
                      ? `${NAV_PATH.assistant}?thread=${encodeURIComponent(state.threadId)}`
                      : NAV_PATH.assistant,
                  )
                }
              />
            </>
          }
          status={
            day.isError ? (
              <DayError
                error={day.error}
                onRetry={() => lastDay.current && set({ day: lastDay.current })}
                onMyAgents={() => leave(NAV_PATH.myAgents)}
              />
            ) : (
              parts.status
            )
          }
          footer={parts.dock}
        >
          {parts.feed}
        </SidePanel>
      )}
    </Conversation>
  );
}
